import { FC, ReactNode, useState } from 'react'
import { Card, Divider, Drawer, Flex, Space, Tag, Typography, theme } from 'antd'
import type { CSSProperties } from 'react'
import Shipment, { windingsDisplay } from '../../../../types/api/shipment'
import ShipmentPosition from '../../../../types/api/shipment-position'
import { MaterialRequirement } from '../../../../types/api/components/material/material-requirements'
import { hasDmsInFinishing } from '../../../../helpers/is-dms-machine'
import StampImageLink from '../../../../components/stamp-image-link/stamp-image-link'
import formatIntegerBySpace from '../../../../helpers/formatters/format-integer-by-space'
import { formatRollerDisplay } from '../../../../helpers/format-roller-display'

interface ShipmentHeadingCellProps {
    shipment: Shipment
}

const commentTextStyle: CSSProperties = {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
}

const detailsTriggerStyle = (color: string): CSSProperties => ({
    cursor: 'pointer',
    color,
    borderBottom: `1px dashed ${color}`,
})

function formatMachines(shipment: Shipment): string {
    const names = [
        shipment.machine?.name.split(' ')[0],
        ...(shipment.additional_machines?.map(x => x.name.split(' ')[0]) ?? []),
    ].filter(Boolean)

    return names.length ? names.join(', ') : '—'
}

function getPositionsWithComments(positions?: ShipmentPosition[]): ShipmentPosition[] {
    return positions?.filter(position => position.comment?.trim()) ?? []
}

function formatMaterialRequirement(requirement: MaterialRequirement): string {
    const name = requirement.material_type?.name || '—'
    const widths = [
        ...new Set(
            (requirement.configurations ?? [])
                .map(config => config.material?.width)
                .filter((width): width is number => width != null)
        ),
    ]

    return widths.length ? `${name} (${widths.join(', ')} мм)` : name
}

function getMaterialsDisplay(shipment: Shipment): { main?: string; additional: string[] } {
    const requirements = shipment.materialRequirements
    if (!requirements?.length) return { additional: [] }

    const [main, ...additional] = requirements
    return {
        main: formatMaterialRequirement(main),
        additional: additional.map(formatMaterialRequirement),
    }
}

function formatLacquers(shipment: Shipment): string {
    const items = shipment.lacquers
        ?.map(lacquer => [lacquer.type?.name, lacquer.name].filter(Boolean).join(' - '))
        .filter(Boolean)

    return items?.length ? items.join(', ') : '—'
}

/** Параметры штампа для отображения: при «Без высечки» — из virtualStamp; ручьи — менеджера (не штампа). */
function getStampDisplay(shipment: Shipment) {
    if (shipment.withoutCutting) {
        const virtualStamp = shipment.virtualStamp
        return {
            number: 'Без высечки',
            width: virtualStamp?.width,
            length: virtualStamp?.length,
            roller: formatRollerDisplay(virtualStamp?.roller?.roller),
            floods: virtualStamp?.floods,
            range: virtualStamp?.range,
        }
    }

    return {
        number: shipment.stamp?.number,
        width: shipment.stamp?.width,
        length: shipment.stamp?.length,
        roller: formatRollerDisplay(shipment.stamp?.roller?.roller),
        floods: shipment.stampFloods,
        range: shipment.stamp?.range,
    }
}

function YnTag({ value }: { value: boolean | undefined }) {
    if (value == null) return <Typography.Text type='secondary'>—</Typography.Text>
    return (
        <Tag color={value ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
            {value ? 'Да' : 'Нет'}
        </Tag>
    )
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <Flex gap={16} justify='space-between' align='flex-start' wrap='nowrap'>
            <Typography.Text type='secondary' style={{ flexShrink: 0 }}>
                {label}
            </Typography.Text>
            <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>
                {value == null || value === '' ? '—' : value}
            </span>
        </Flex>
    )
}

function InfoSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Card size='small' title={title}>
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
                {children}
            </Space>
        </Card>
    )
}

const ShipmentHeadingCell: FC<ShipmentHeadingCellProps> = ({ shipment }) => {
    const { token } = theme.useToken()
    const [detailsOpen, setDetailsOpen] = useState(false)
    const manager = shipment.user_audit?.created_by_permissions_user?.username
    const additionalJobs = shipment.additional_jobs?.map(x => x.name).join(', ')
    const commentPositions = getPositionsWithComments(shipment.shipment_positions)
    const hasComments = Boolean(shipment.comment?.trim()) || commentPositions.length > 0
    const materials = getMaterialsDisplay(shipment)
    const stamp = getStampDisplay(shipment)
    const stampSize =
        stamp.width != null && stamp.length != null
            ? `${stamp.width}x${stamp.length}`
            : undefined

    return (
        <Flex vertical gap={4} style={{ fontSize: 12 }}>
            <Flex gap={18} align='center' wrap='wrap'>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                    {shipment.client?.name || '—'}
                </span>

                <Typography.Text>Заказ №{shipment.id}</Typography.Text>

                {manager ? (
                    <Typography.Text>Менеджер: {manager}</Typography.Text>
                ) : null}
            </Flex>

            <Flex gap={18} align='center' wrap='wrap'>
                <Flex gap={4} align='center'>
                    <Typography.Text>Штамп: {stamp.number ?? '—'}</Typography.Text>
                    {!shipment.withoutCutting && shipment.stamp ? (
                        <StampImageLink stamp={shipment.stamp} />
                    ) : null}
                </Flex>

                <Typography.Text>Размеры (ШхД): {stampSize ?? '—'}</Typography.Text>

                <Typography.Text>Раппорт: {stamp.roller ?? '—'}</Typography.Text>

                <Typography.Text>Ручьи: {stamp.floods ?? '—'}</Typography.Text>

                <Typography.Text>М/э: {stamp.range ?? '—'}</Typography.Text>

                <Typography.Text>Станки: {formatMachines(shipment)}</Typography.Text>

                <Typography.Text>
                    Схема намотки (л/о): {windingsDisplay(shipment.windingSchemeFace)} /{' '}
                    {windingsDisplay(shipment.windingSchemeTurn)}
                </Typography.Text>

                {additionalJobs ? (
                    <Typography.Text>Доп. работы: {additionalJobs}</Typography.Text>
                ) : null}

                {shipment.shipment_positions?.length ? (
                    <Typography.Text>Видов: {shipment.shipment_positions.length}</Typography.Text>
                ) : null}

                <span
                    style={detailsTriggerStyle(token.colorWarning)}
                    onClick={() => setDetailsOpen(true)}
                >
                    Подробная информация
                </span>
            </Flex>

            <Drawer
                title={
                    <Flex vertical gap={2}>
                        <Typography.Text strong>Информация о заказе</Typography.Text>
                        <Typography.Text type='secondary'>
                            №{shipment.id}
                            {shipment.client?.name ? ` · ${shipment.client.name}` : ''}
                        </Typography.Text>
                    </Flex>
                }
                width={420}
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
            >
                <Space direction='vertical' size={16} style={{ width: '100%' }}>
                    <InfoSection title='Заказ'>
                        <InfoRow label='Заказ №' value={shipment.id} />
                        {manager ? <InfoRow label='Менеджер' value={manager} /> : null}
                    </InfoSection>

                    <InfoSection title='Материалы'>
                        {materials.main ? (
                            <Space direction='vertical' size={2} style={{ width: '100%' }}>
                                <Typography.Text type='secondary'>Основной</Typography.Text>
                                <Typography.Text style={{ wordBreak: 'break-word' }}>
                                    {materials.main}
                                </Typography.Text>
                            </Space>
                        ) : (
                            <Typography.Text type='secondary'>—</Typography.Text>
                        )}

                        {materials.additional.map((item, index) => (
                            <Space
                                key={`${item}-${index}`}
                                direction='vertical'
                                size={2}
                                style={{ width: '100%' }}
                            >
                                <Typography.Text type='secondary'>
                                    {materials.additional.length > 1
                                        ? `Доп. ${index + 1}`
                                        : 'Доп.'}
                                </Typography.Text>
                                <Typography.Text style={{ wordBreak: 'break-word' }}>
                                    {item}
                                </Typography.Text>
                            </Space>
                        ))}
                    </InfoSection>

                    <InfoSection title='Штамп'>
                        <InfoRow label='Штамп' value={stamp.number} />
                        <InfoRow label='Размеры (ШхД)' value={stampSize} />
                        <InfoRow label='Раппорт' value={stamp.roller} />
                        <InfoRow label='Ручьи' value={stamp.floods} />
                        <InfoRow label='М/э' value={stamp.range} />
                    </InfoSection>

                    <InfoSection title='Производство'>
                        <InfoRow label='Станки' value={formatMachines(shipment)} />
                        {additionalJobs ? (
                            <InfoRow label='Доп. работы' value={additionalJobs} />
                        ) : null}
                        {shipment.shipment_positions?.length ? (
                            <InfoRow
                                label='Видов'
                                value={shipment.shipment_positions.length}
                            />
                        ) : null}
                        <InfoRow
                            label='Схема намотки'
                            value={`${windingsDisplay(shipment.windingSchemeFace)} / ${windingsDisplay(shipment.windingSchemeTurn)}`}
                        />
                    </InfoSection>

                    {shipment.shipment_positions?.length ? (
                        <InfoSection title='Позиции'>
                            {shipment.shipment_positions.map((position, index) => (
                                <Space
                                    key={position.documentId || position.id || index}
                                    direction='vertical'
                                    size={6}
                                    style={{ width: '100%' }}
                                >
                                    {index > 0 ? <Divider style={{ margin: '4px 0' }} /> : null}
                                    <Typography.Text strong>
                                        {position.labelName || `Вид ${index + 1}`}
                                    </Typography.Text>
                                    <InfoRow
                                        label='Ручьи в сборке'
                                        value={position.floodsInAccumulation ?? '—'}
                                    />
                                    <InfoRow
                                        label='Тираж'
                                        value={
                                            position.copies != null
                                                ? formatIntegerBySpace(position.copies)
                                                : '—'
                                        }
                                    />
                                </Space>
                            ))}
                        </InfoSection>
                    ) : null}

                    <InfoSection title='Отделка'>
                        <InfoRow label='Конгрев' value={<YnTag value={shipment.congreve} />} />
                        <InfoRow label='Лаки' value={formatLacquers(shipment)} />
                        <InfoRow
                            label='Праймировать материал'
                            value={<YnTag value={shipment.priming} />}
                        />
                        <InfoRow
                            label='Отделка на DMS'
                            value={
                                <YnTag
                                    value={hasDmsInFinishing(shipment.additional_machines)}
                                />
                            }
                        />
                    </InfoSection>

                    {hasComments ? (
                        <InfoSection title='Комментарии'>
                            {shipment.comment?.trim() ? (
                                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                                    <Typography.Text type='secondary'>
                                        К отгрузке
                                    </Typography.Text>
                                    <Typography.Text style={commentTextStyle}>
                                        {shipment.comment}
                                    </Typography.Text>
                                </Space>
                            ) : null}

                            {commentPositions.map((position, index) => (
                                <div key={position.documentId || position.id}>
                                    {(index > 0 || shipment.comment?.trim()) && (
                                        <Divider style={{ margin: '8px 0' }} />
                                    )}
                                    <Space direction='vertical' size={4} style={{ width: '100%' }}>
                                        <Typography.Text type='secondary'>
                                            {position.labelName || 'Позиция'}
                                        </Typography.Text>
                                        <Typography.Text style={commentTextStyle}>
                                            {position.comment}
                                        </Typography.Text>
                                    </Space>
                                </div>
                            ))}
                        </InfoSection>
                    ) : null}
                </Space>
            </Drawer>
        </Flex>
    )
}

export default ShipmentHeadingCell
